import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Input,
  PageContainer,
  SurfaceCard,
  Table,
} from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { User, ActivePlatform } from '../../domain/entities/user.entity';
import { useUsers } from '../hooks/use-users';
import { CreateUserModal } from '../components/CreateUserModal';
import {
  useCanAdministerUser,
  useIsScopedProvisioner,
} from '../hooks/use-user-permissions';
import { ScopedAccessButton } from '../components/ScopeSelect';
import { useProvisionableScopes } from '@/shared/scopes/use-scopes';
import { RefreshButton } from '@/shared/RefreshButton';
import { platformBadge } from '@/shared/scopes/platform-colors';
import {
  readPageParam,
  writePageParam,
} from '@/shared/hooks/use-page-query-param';

function PlatformBadge({ platform }: { platform: ActivePlatform }) {
  const { color: bgClass, initial } = platformBadge(platform.platform);

  return (
    <div className="group relative inline-flex">
      <span
        className={`flex h-7 w-7 cursor-default items-center justify-center rounded-full text-[10px] font-bold text-white ${bgClass} ${
          platform.isActive ? 'opacity-100' : 'opacity-40'
        }`}
      >
        {initial}
      </span>
      <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-neutral-900 px-2.5 py-1.5 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 dark:bg-neutral-700">
        <span className="font-medium">{platform.platformName}</span>
        <span className="mx-1.5 text-neutral-400">·</span>
        <span
          className={platform.isActive ? 'text-green-400' : 'text-neutral-400'}
        >
          {platform.isActive ? 'Ativa' : 'Inativa'}
        </span>
        <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-neutral-900 dark:border-t-neutral-700" />
      </div>
    </div>
  );
}

function formatDate(isoString: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(isoString));
}

const columns: ColumnDef<User>[] = [
  {
    key: 'name',
    header: 'Nome',
    sortable: true,
  },
  {
    key: 'email',
    header: 'E-mail',
    sortable: true,
  },
  {
    key: 'createdAt',
    header: 'Data de Criação',
    sortable: true,
    render: (row) => (
      <span className="text-neutral-600 dark:text-neutral-400">
        {formatDate(row.createdAt)}
      </span>
    ),
  },
  {
    key: 'activePlatforms',
    header: 'Plataformas',
    render: (row) =>
      row.activePlatforms.length === 0 ? (
        <span className="text-xs text-neutral-400">—</span>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {row.activePlatforms.map((p) => (
            <PlatformBadge key={p.scopeId} platform={p} />
          ))}
        </div>
      ),
  },
];

const PER_PAGE = 10;

export function UsersPage() {
  const navigate = useNavigate();
  // Página e e-mail filtrado vivem na URL (não em useState) para sobreviver a
  // "Voltar" do detalhe do usuário — ver
  // primum-core-root/docs/plans/done/admin-dashboard-pagination-url-sync.md. `emailFilter` continua
  // local: é o valor bruto exibido a cada tecla, só o valor "assentado" (pós
  // debounce) vai pra URL — e junto com ele, o reset de página, no mesmo
  // setSearchParams (ver aviso em use-page-query-param.ts).
  const [searchParams, setSearchParams] = useSearchParams();
  const page = readPageParam(searchParams);
  const debouncedEmail = searchParams.get('email') ?? '';
  const [emailFilter, setEmailFilter] = useState(debouncedEmail);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const [sortColumn, setSortColumn] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  /**
   * O admin cria pessoa com qualquer acesso; o `iamEditor` só traz gente para
   * as plataformas que administra, e por isso a lista dele roda aqui.
   */
  const canAdminister = useCanAdministerUser();
  const scopedProvisioner = useIsScopedProvisioner();
  const provisionableScopes = useProvisionableScopes(scopedProvisioner);

  const { data, isLoading, refetch, isFetching } = useUsers(
    debouncedEmail || undefined,
    page,
    PER_PAGE,
  );

  const totalPages = data ? Math.ceil(data.total / PER_PAGE) : 1;

  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writePageParam(params, next);
        return params;
      },
      { replace: true },
    );
  };

  const commitEmailAndResetPage = (email: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (!email) params.delete('email');
        else params.set('email', email);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailFilter(value);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(
      () => commitEmailAndResetPage(value),
      400,
    );
  };

  const handleSort = (column: string, direction: 'asc' | 'desc' | null) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const sortedData = (() => {
    if (!data?.items || !sortColumn || !sortDirection) return data?.items ?? [];
    return [...data.items].sort((a, b) => {
      const aVal = String((a as Record<string, unknown>)[sortColumn] ?? '');
      const bVal = String((b as Record<string, unknown>)[sortColumn] ?? '');
      const cmp = aVal.localeCompare(bVal, 'pt-BR');
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  })();

  return (
    <PageContainer maxWidth="6xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-(--color-text-primary) text-2xl font-semibold">
            Usuários
          </h1>
          <p className="text-(--color-text-secondary) mt-1 text-sm">
            Consulte e filtre os usuários cadastrados na plataforma.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
          {canAdminister && (
            <Button onClick={() => setIsCreateOpen(true)}>Criar usuário</Button>
          )}
          {scopedProvisioner && (
            <ScopedAccessButton
              source={provisionableScopes}
              onClick={() => setIsCreateOpen(true)}
            >
              Criar usuário
            </ScopedAccessButton>
          )}
        </div>
      </div>

      {(canAdminister || scopedProvisioner) && (
        <CreateUserModal
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          provisionableScopes={
            scopedProvisioner ? provisionableScopes : undefined
          }
        />
      )}

      <SurfaceCard className="space-y-5">
        <div className="max-w-sm">
          <Input
            placeholder="Filtrar por e-mail..."
            value={emailFilter}
            onChange={handleEmailChange}
            leftIcon={
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Z"
                />
              </svg>
            }
          />
        </div>

        <Table
          columns={columns}
          data={sortedData}
          keyExtractor={(row) => row.accountId}
          loading={isLoading}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          emptyMessage="Nenhum usuário encontrado."
          striped
          onRowClick={(row) => void navigate(`/management/users/${row.userId}`)}
        />

        <div className="flex items-center justify-between">
          <p className="text-(--color-text-secondary) text-xs">
            {data ? (
              <>
                {data.total} {data.total === 1 ? 'usuário' : 'usuários'}{' '}
                encontrado
                {data.total !== 1 ? 's' : ''}
              </>
            ) : null}
          </p>

          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-(--color-text-secondary) text-xs">
                {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </SurfaceCard>
    </PageContainer>
  );
}
